import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  type NCRColumnConfig,
  loadNCRColumns,
  renderNCRCell,
} from '../components/quality/qualityColumnConfig';
import { QualityColumnsDropdown } from '../components/quality/QualityColumnsDropdown';
import NCRQuickFilters, { NCR_QUICK_MODES, type NCRQuickMode } from '../components/quality/NCRQuickFilters';
import NCRRowActions from '../components/quality/NCRRowActions';
import * as XLSX from 'xlsx';
import { companyNcrReference, capitaliseSeverity } from '../lib/ncrReference';
import NCRFiltersBar, {
  LIABILITY_OPTIONS,
  CLAIM_STATUS_OPTIONS,
  NCR_STATUS_OPTIONS,
  getRollingTwelveMonthsRange,
} from '../components/quality/NCRFiltersBar';
import ColumnHeaderFilter, { type ColumnFilterDescriptor } from '../components/quality/ColumnHeaderFilter';
// CR-5887 (TEMPORARY — remove after testing): admin-only on-demand trigger.
import { SendQualitySummaryTestButton } from '../components/quality/SendQualitySummaryTestButton';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import NCRPODeepLinkChip, { usePODeepLink } from '../components/quality/NCRPODeepLinkChip';
import { useView } from '../contexts/ViewContext';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  FileSpreadsheet,
  Filter,
  ChevronDown,
  ChevronUp,
  Check,
  X,
} from 'lucide-react';
import {
  qualityAPI,
  platformSettingsAPI,
  fetchAllPages,
  NonConformanceReport,
  QualityTabsVisibility,
} from '../lib/api';
import { cn } from '../lib/utils';
import { Pagination } from '../components/ui/pagination';
import { usePagination } from '../hooks';
import { getErrorMessage } from '../hooks/useFetchData';
import { useFormatters } from '../contexts/PreferencesContext';
import { SectionTourTrigger } from '../components/tour/SectionTourTrigger';
import { useIsCustomer } from '../hooks/usePortalMode';
import { toast } from 'sonner';

/**
 * CR-5887 rework: human labels for the Daily Quality Summary email deep-link
 * buckets, used by the active-filter banner so the user can see which row
 * they clicked. Keys mirror the ?next_action= / ?reject= URL values and the
 * email row wording exactly.
 */
const NEXT_ACTION_LABELS: Record<string, string> = {
  awaiting_supplier: 'Awaiting Supplier',
  awaiting_customer: 'Awaiting Customer',
};
const REJECT_LABELS: Record<string, string> = {
  pending: 'Reject requested — awaiting customer approval',
  declined: 'Reject requested — declined by customer',
  accepted: 'Reject requested — accepted by customer',
};

export default function Quality() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // CR-7304: opened from a PO header's NCR card — narrowed to that order.
  const poDeepLink = usePODeepLink();
  const isCustomer = useIsCustomer();
  const { viewKey, getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const { user } = useAuth();
  const { formatDate, formatNumber } = useFormatters();
  const [columns, setColumns] = useState<NCRColumnConfig[]>(loadNCRColumns);
  // 8D/A3 scoring master switch (Admin Setup > Features). When off, the
  // RC/CA/8D-A3 score columns are hidden from the register and the column
  // manager. Persistence is unaffected — loadNCRColumns always rebuilds the
  // full column set from DEFAULT_NCR_COLUMNS, so the columns return when the
  // feature is re-enabled.
  const { enable8dScoring } = useMenuSettings();
  const SCORING_COL_IDS = ['rc_score', 'ca_score', 'score_8d', 'close_off_days_8d'];
  const effectiveColumns = useMemo(
    () => (enable8dScoring ? columns : columns.filter((c) => !SCORING_COL_IDS.includes(c.id))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, enable8dScoring],
  );
  const visibleCols = useMemo(() => effectiveColumns.filter((c) => c.visible), [effectiveColumns]);
  const [ncrs, setNcrs] = useState<NonConformanceReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('ncr_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // CR-5887: if the page is opened via a deep-link from the Daily
  // Quality Summary email, the filter panel is expanded by default so
  // the user can see why the list is scoped.
  // CR-5766: Also expand if supplier or liability param is present (from Performance KPI click-through)
  // CR-6310: Also expand if date_from/date_to params are present (from Performance tab drill-through)
  const [showFilters, setShowFilters] = useState(
    Boolean(searchParams.get('status') || searchParams.get('reject') || searchParams.get('next_action') || searchParams.get('supplier') || searchParams.get('liability') || searchParams.get('date_from') || searchParams.get('date_to'))
  );
  const [exporting, setExporting] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter states. CR-5887: seed from URL params so deep-links from the
  // Daily Quality Summary email land on the right filtered view.
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [rejectFilter, setRejectFilter] = useState(searchParams.get('reject') ?? '');
  // CR-5887 rework: the Daily Quality Summary email's "Awaiting Supplier /
  // Customer response" rows deep-link to ?next_action=<bucket>. Seed from the
  // URL so the list lands filtered to match the count the user clicked.
  const [nextActionFilter, setNextActionFilter] = useState(searchParams.get('next_action') ?? '');
  // Archived NCRs are out of every list by default; the Archived chip
  // sets this to 'true' to ask for them explicitly.
  const [archivedFilter, setArchivedFilter] = useState('');
  const [countsRefreshKey, setCountsRefreshKey] = useState(0);
  // Default the list to a rolling last-12-months window (the "From" date is
  // pre-filled, and the counts read the same value). Clearing it widens to
  // all-time. Deep-links from the Daily Quality Summary email keep all-time so
  // the email's count still equals the rows shown.
  // Shared with the filter panel's "Last 12 Months (Default)" chip so the
  // chip re-applies exactly the range the page loads with.
  const twelveMonthsAgoISO = () => getRollingTwelveMonthsRange().dateFrom;
  // CR-6310: Include date_from/date_to in deep-link check for Performance tab drill-through
  const hasDeepLinkFilter = Boolean(
    searchParams.get('status') || searchParams.get('reject') || searchParams.get('next_action') ||
    searchParams.get('date_from') || searchParams.get('date_to') || searchParams.get('po'),
  );
  // CR-6310: Seed date filters from URL params for Performance tab drill-through
  const [dateFrom, setDateFrom] = useState(
    searchParams.get('date_from') ?? (hasDeepLinkFilter ? '' : twelveMonthsAgoISO())
  );
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') ?? '');
  // CR-5550 round 2: surface the legacy Template-2 filter set in the UI.
  // Backend filters already exist (CR-5536 + CR-5550 round 1); these are
  // the controls that finally drive them.
  // CR-5766: Seed supplierFilter from URL param for Performance KPI click-through
  const [siteFilter, setSiteFilter] = useState('');
  const [supplierGroupFilter, setSupplierGroupFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get('supplier') ?? '');
  const [commodityGroupFilter, setCommodityGroupFilter] = useState('');
  const [commodityTypeFilter, setCommodityTypeFilter] = useState('');
  const [strategicFilter, setStrategicFilter] = useState(false);
  // Priority-1 NCR list filters.
  const [partNumberFilter, setPartNumberFilter] = useState('');
  // Quality Engineer column filter. Backed by the supplier's quality-engineer
  // master data field, so it filters server-side across the whole dataset.
  const [qualityEngineerFilter, setQualityEngineerFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  // CR-5766: Seed liabilityFilter from URL param for Performance KPI click-through
  const [liabilityFilter, setLiabilityFilter] = useState(searchParams.get('liability') ?? '');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [claimStatusFilter, setClaimStatusFilter] = useState('');
  const [actionStatusFilter, setActionStatusFilter] = useState('');
  const [collectionDateFrom, setCollectionDateFrom] = useState('');
  const [collectionDateTo, setCollectionDateTo] = useState('');

  // Phase 1 Excel-style per-column filters. Each entry reuses an existing
  // server-side NCR filter (NCRFilterSet) so the funnel in a column header
  // filters the whole dataset, not just the visible page — and stays in sync
  // with NCRFiltersBar because both write the same state.
  const columnFilters: Record<string, ColumnFilterDescriptor> = {
    status_display: { type: 'choice', value: statusFilter, onChange: setStatusFilter, options: NCR_STATUS_OPTIONS, allLabel: 'All Statuses' },
    liability_display: { type: 'choice', value: liabilityFilter, onChange: setLiabilityFilter, options: LIABILITY_OPTIONS, allLabel: 'All Liabilities' },
    is_credit_required: { type: 'choice', value: claimStatusFilter, onChange: setClaimStatusFilter, options: CLAIM_STATUS_OPTIONS, allLabel: 'All Claim Statuses' },
    part_number: { type: 'text', value: partNumberFilter, onChange: setPartNumberFilter },
    supplier_quality_engineer: { type: 'text', value: qualityEngineerFilter, onChange: setQualityEngineerFilter },
  };

  // NCR Template preference (1 = Mandatory 8D, 2 = Optional 8D)
  const [ncrTemplate, setNcrTemplate] = useState<1 | 2>(1);

  // Quality tab visibility from platform settings
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // CR-6385: the tab needs BOTH the admin's tab-visibility choice and the
  // concession feature switch. The first is layout, the second is whether the
  // tenant runs concessions at all — a tenant that never opted in must not be
  // one settings toggle away from the workflow.
  // Sourced from this page's own settings fetch, not MenuSettingsContext: the
  // context loads separately and starts false, so reading the two together
  // raced and could redirect a tenant away from a tab it is entitled to.
  const [concessionsEnabled, setConcessionsEnabled] = useState(false);
  const showConcessionsTab = tabVisibility.concessions && concessionsEnabled;

  const pagination = usePagination({
    pageSize: 15,
    onPageChange: () => {}, // Fetch is triggered by useEffect
  });

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      pagination.reset();
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Map frontend display fields to backend ordering fields
  const fieldMapping: Record<string, string> = {
    id: 'id',
    company_name: 'company__name',
    ncr_number: 'ncr_number',
    supplier_name: 'supplier__name',
    part_number: 'part_number',
    grn_number: 'grn_number',
    ncr_date: 'ncr_date',
    status: 'status',
  };

  type SortableField = keyof typeof fieldMapping;

  const handleSort = (field: SortableField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    pagination.reset();
  };

  const getSortIcon = (field: SortableField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const backendField = fieldMapping[sortField] || sortField;
      const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;
      const viewParams = getViewQueryParams();
      const ncrsResponse = await qualityAPI.getNCRs({
        ordering,
        page: pagination.currentPage,
        page_size: pagination.pageSize,
        search: debouncedSearchTerm || undefined,
        status: statusFilter || undefined,
        // CR-5887: 'reject' bucket from the Daily Quality Summary email
        // (pending | declined | accepted) drills into the action-log
        // backed filter on the NCR viewset.
        reject: rejectFilter || undefined,
        // CR-5887 rework: 'next_action' bucket (awaiting_supplier |
        // awaiting_customer) maps to the NCR status set that backs the
        // email's count, so the filtered list matches the clicked number.
        next_action: nextActionFilter || undefined,
        // Archived NCRs are hidden unless the Archived chip is active. The
        // backend hides them by default, so this is only ever sent as 'true'.
        archived: archivedFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        // CR-5550 round 2: pipe the legacy Template-2 filters through to
        // the backend (params shipped in CR-5536 + CR-5550 round 1).
        site: siteFilter || undefined,
        'supplier__group': supplierGroupFilter || undefined,
        supplier: supplierFilter || undefined,
        commodity_group: commodityGroupFilter || undefined,
        commodity_type: commodityTypeFilter || undefined,
        strategic: strategicFilter ? true : undefined,
        // Priority-1 list filters.
        part_number__icontains: partNumberFilter || undefined,
        // CR-6794: the Quality Engineer column is resolved per customer, so
        // the filter is no longer a plain supplier-field traversal. The old
        // `supplier__quality_engineer__icontains` param still routes to the
        // same backend method for saved links.
        quality_engineer: qualityEngineerFilter || undefined,
        region: regionFilter || undefined,
        liability_type: liabilityFilter || undefined,
        warehouse: warehouseFilter || undefined,
        claim_status: claimStatusFilter || undefined,
        action_status: actionStatusFilter || undefined,
        collection_date_from: collectionDateFrom || undefined,
        collection_date_to: collectionDateTo || undefined,
        po: poDeepLink.poId || undefined,
        ...viewParams,
      });

      setNcrs(ncrsResponse.data.results);
      pagination.setFromResponse(ncrsResponse.data.count, (ncrsResponse.data.results || []).length);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load quality data'));
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, debouncedSearchTerm, sortField, sortDirection, statusFilter, rejectFilter, nextActionFilter, archivedFilter, dateFrom, dateTo, siteFilter, supplierGroupFilter, supplierFilter, commodityGroupFilter, commodityTypeFilter, strategicFilter, partNumberFilter, qualityEngineerFilter, regionFilter, liabilityFilter, warehouseFilter, claimStatusFilter, actionStatusFilter, collectionDateFrom, collectionDateTo, poDeepLink.poId, isCustomer, viewKey, getViewQueryParams]);

  useEffect(() => {
    if (viewContextLoading) return;
    fetchData();
  }, [fetchData, viewContextLoading, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // Fetch platform settings for NCR template preference and tab visibility
  useEffect(() => {
    const fetchPlatformSettings = async () => {
      try {
        // Fetch display settings for tab visibility
        const displayResponse = await platformSettingsAPI.getDisplaySettings();
        if (displayResponse.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...displayResponse.data.quality_tabs }));
        }
        // CR-6385: concession master switch, from the same payload.
        if (displayResponse.data) {
          setConcessionsEnabled(!!displayResponse.data.concessions_enabled);
        }

        // Fetch full settings for NCR template
        const response = await platformSettingsAPI.getSettings();
        const template = response.data.ncr_template;
        if (template === 1 || template === 2) {
          setNcrTemplate(template);
        }
      } catch (error) {
        console.error('Error fetching platform settings:', error);
      } finally {
        setTabSettingsLoaded(true);
      }
    };
    fetchPlatformSettings();
  }, []);

  // Redirect if NCRs tab is disabled - navigate to first available quality tab
  useEffect(() => {
    if (!tabSettingsLoaded) return;

    if (!tabVisibility.ncrs) {
      // NCRs disabled - redirect to first available tab
      if (showConcessionsTab) {
        navigate('/concessions', { replace: true });
      } else if (tabVisibility.approval_register && isCustomer) {
        navigate('/intelligence/qualifications', { replace: true });
      }
      // If no tabs are available, stay on page but content won't show
    }
  }, [tabSettingsLoaded, tabVisibility, showConcessionsTab, navigate, isCustomer]);

  const handleViewNCR = (ncrId: number) => {
    navigate(`/quality/ncr/${ncrId}/edit`);
  };

  const handleAddNCR = () => {
    navigate('/quality/ncr/new');
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);

      // Fetch all NCRs (no pagination) for export
      const backendField = fieldMapping[sortField] || sortField;
      const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;

      const viewParams = getViewQueryParams();
      // Walk every page — the backend clamps page_size, so a single large
      // request silently exported at most one page of the filtered set.
      const exportData = await fetchAllPages<NonConformanceReport>((page) => qualityAPI.getNCRs({
        ordering,
        page,
        page_size: 1000,
        search: debouncedSearchTerm || undefined,
        status: statusFilter || undefined,
        // CR-5887 rework: keep the export aligned with the on-screen filtered
        // view for Daily Quality Summary email deep-links.
        reject: rejectFilter || undefined,
        next_action: nextActionFilter || undefined,
        // Archived NCRs are hidden unless the Archived chip is active. The
        // backend hides them by default, so this is only ever sent as 'true'.
        archived: archivedFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        po: poDeepLink.poId || undefined,
        ...viewParams,
      }));

      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }

      // CR-5535: extended column set to match legacy platform export.
      // Added 13 columns Sarah called out as missing: Supplier Code,
      // Buyer, PO Line No, Part Description, Fault Group, Fault Category,
      // Source of Rejection, Response By, Completed By, Selected Root
      // Cause, Corrective Action, Preventive Action, Packing Number No.
      // Underlying fields are now exposed by NonConformanceReportListSerializer.
      //
      // CR-5535 rework (issues 5 & 6): the "Response By" and "Completed
      // By" columns in the legacy SharePoint export were misleadingly
      // labelled — they actually carried CARequiredDate and
      // FRRequiredDate values (see legacy NetworkSQL.cs
      // VIEW_NCRS_NCRLIST_BY_EXCEL: `detail.CARequiredDate ResponseBy,
      // detail.FRRequiredDate CompletedBy`). The Helix backend now
      // computes ca_required_date / car_required_date as
      // raised_date + working-day leadtime so the export columns match
      // what legacy customers saw historically.
      const headers = [
        // Was 'NCR ID' carrying the database primary key — an internal value,
        // unstable across a restore and meaningless in the customer's own
        // systems. Exports the NCR number instead. The separate
        // 'Company NCR/SRN No.' column is kept so the export shape legacy
        // customers match against does not change.
        'NCR No.',
        'Customer',
        'Company NCR/SRN No.',
        'Supplier',
        'Supplier Code',
        'Buyer',
        'Part No.',
        'PO Line No.',
        'Part Description',
        'GRN No.',
        'PO No.',
        'Raised By',
        'NCR Date',
        'Status',
        'Severity',
        'Fault Group',
        'Fault Category',
        'Source of Rejection',
        'Response By',
        'Completed By',
        'Defect Description',
        'Batch Qty',
        'Reject Qty',
        'Shortage Qty',
        'Selected Root Cause',
        'Corrective Action',
        'Preventive Action',
        'Packing Number No.',
      ];

      // The button says "Export to Excel", so write a real .xlsx (same
      // SheetJS path Supplier Performance uses) rather than a CSV with an
      // Excel label. Cells are plain values — no CSV quoting needed.
      const text = (v: unknown): string => String(v ?? '').replace(/\n/g, ' ');

      const rows = exportData.map((ncr) => {
        const values: (string | number)[] = [
          text(ncr.ncr_number),
          text(ncr.customer_name || ncr.company_name),
          // Blank when the customer gave no reference of their own and the
          // stored number is the system-issued NCR##### one.
          companyNcrReference(ncr.ncr_number),
          text(ncr.supplier_name),
          text(ncr.supplier_code),
          text(ncr.buyer_name),
          text(ncr.part_number),
          ncr.po_line_number ?? '',
          text(ncr.part_description),
          text(ncr.grn_number),
          text(ncr.po_number),
          text(ncr.raised_by_name),
          ncr.ncr_date ? formatDate(ncr.ncr_date) : '',
          ncr.status_display || ncr.status,
          ncr.severity_display || capitaliseSeverity(ncr.severity),
          text(ncr.fault_group_name),
          text(ncr.fault_category_name),
          text(ncr.source_of_rejection_display || ncr.source_of_rejection),
          ncr.ca_required_date ? formatDate(ncr.ca_required_date) : '',
          ncr.car_required_date ? formatDate(ncr.car_required_date) : '',
          text(ncr.defect_description),
          ncr.batch_qty || 0,
          ncr.reject_qty || 0,
          ncr.shortage_qty || 0,
          text(ncr.selected_root_cause),
          text(ncr.corrective_action_summary),
          text(ncr.preventive_action),
          text(ncr.packing_note_no),
        ];
        return values;
      });

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'NCRs');
      XLSX.writeFile(workbook, `NCR_Export_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast.success(`Exported ${exportData.length} NCRs to Excel`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export NCRs');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setStatusFilter('');
    setRejectFilter('');
    setNextActionFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    setDebouncedSearchTerm('');
    // CR-5550 round 2
    setSiteFilter('');
    setSupplierGroupFilter('');
    setSupplierFilter('');
    setCommodityGroupFilter('');
    setCommodityTypeFilter('');
    setStrategicFilter(false);
    // Priority-1 list filters.
    setPartNumberFilter('');
    setQualityEngineerFilter('');
    setRegionFilter('');
    setLiabilityFilter('');
    setWarehouseFilter('');
    setClaimStatusFilter('');
    setActionStatusFilter('');
    setCollectionDateFrom('');
    setCollectionDateTo('');
    // CR-5887: also drop the URL params so a refresh doesn't re-apply them.
    setSearchParams({});
    pagination.reset();
  };

  const getStatusBadge = (status: string, statusDisplay: string) => {
    // Match old SupplierPortal styling
    let badgeClass = '';

    if (status === 'closed') {
      badgeClass = 'bg-[#d4edda] text-[#155724] border-[#28a745]';
    } else if (status === 'deleted') {
      badgeClass = 'bg-[#f8d7da] text-[#721c24] border-[#dc3545]';
    } else {
      // Open statuses (draft, created, ca_submitted, car_submitted)
      badgeClass = 'bg-[#e9ecef] text-[#495057] border-[#6c757d]';
    }

    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${badgeClass}`}>
        {statusDisplay}
      </span>
    );
  };

  const getNextAction = (ncr: NonConformanceReport) => {
    // CR-5542: action ownership is derived from liability_type (the
    // SP-equivalent ownership signal) on top of status. Helix's
    // liability_type values are 1 = Supplier Liability, 2 = Customer
    // Liability (swapped from SP's NCREnums; 0 = Not Determined is a
    // Helix-only addition for the customer-still-deciding phase).
    //
    // Supplier owns the next action when:
    //   - liability_type = 1 (Supplier owns the NCR), OR
    //   - liability_type = 2 (Customer) AND is_collection_required (supplier
    //     has to participate in collection logistics).
    // Otherwise (Not Determined, or Customer Liability with no collection)
    // the action stays with the customer — they're either still deciding,
    // or it's a wholly internal customer matter that they need to close.
    const supplierOwns =
      ncr.liability_type === 1 ||
      (ncr.liability_type === 2 && ncr.is_collection_required);

    if (ncr.status === 'draft') {
      return 'Awaiting Customer';
    } else if (ncr.status === 'created') {
      return supplierOwns ? 'Awaiting Supplier' : 'Awaiting Customer';
    } else if (ncr.status === 'ca_submitted') {
      // CAR is a supplier-side action by definition — only reached on
      // NCRs where the supplier already owns the workflow.
      return 'Awaiting Supplier';
    } else if (ncr.status === 'car_submitted') {
      return 'Awaiting Customer';
    } else if (ncr.status === 'closed') {
      return 'Closed';
    } else if (ncr.status === 'deleted') {
      return 'Deleted';
    }
    return '-';
  };

  // Get step number based on NCR status. CR-5980: the stepper steps map to
  // workflow milestones (Created → CA → CAR → Close). Both 'draft' and
  // 'created' map to step 1 because the supplier hasn't submitted yet.
  const getStepFromStatus = (status: string): number => {
    switch (status) {
      case 'draft': return 1;
      case 'created': return 1;
      case 'ca_submitted': return 2;
      case 'car_submitted': return 3;
      case 'closed': return 4;
      default: return 1;
    }
  };

  // Mini progress indicator - Template 1 (numbered steps)
  const MiniProgressTemplate1 = ({ status }: { status: string }) => {
    const currentStep = getStepFromStatus(status);
    const steps = [
      { num: 1, label: 'Created' },
      { num: 2, label: 'CA' },
      { num: 3, label: 'CAR' },
      { num: 4, label: 'Close' },
    ];

    return (
      <div className="flex items-center gap-0.5">
        {steps.map((step, index) => {
          const isCompleted = currentStep > step.num;
          const isCurrent = currentStep === step.num;
          return (
            <div key={step.num} className="flex items-center">
              <div
                className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold',
                  isCompleted
                    ? 'bg-green-600 text-white'
                    : isCurrent
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                )}
                title={step.label}
              >
                {isCompleted ? <Check className="w-3 h-3" /> : step.num}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'w-3 h-0.5',
                    isCompleted ? 'bg-green-600' : 'bg-gray-200'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Mini progress indicator - Template 2 (emoji steps)
  const MiniProgressTemplate2 = ({ status, ncr }: { status: string; ncr: NonConformanceReport }) => {
    const currentStep = getStepFromStatus(status);
    const liabilityType = ncr.liability_type ?? 0;
    const liabilityDetermined = liabilityType > 0;
    const steps = [
      { icon: '📋', label: 'Created', active: true },
      { icon: liabilityType === 1 ? '🏭' : liabilityType === 2 ? '🏢' : '⚖️', label: 'Liability', active: liabilityDetermined },
      { icon: '⚠️', label: '8D', active: true },
      ...(ncr.is_collection_required ? [{ icon: '📦', label: 'Collect', active: true }] : []),
      ...(ncr.is_credit_required ? [{ icon: '💰', label: 'Credit', active: true }] : []),
      { icon: '✅', label: 'Closed', active: true },
    ];

    return (
      <div className="flex items-center gap-1">
        {steps.map((step, index) => {
          const isCompleted = currentStep > index + 1;
          const isCurrent = currentStep === index + 1;
          return (
            <div key={index} className="flex items-center">
              <div
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-sm',
                  isCompleted || isCurrent
                    ? 'bg-green-100'
                    : 'bg-gray-100 opacity-50'
                )}
                title={step.label}
              >
                {step.icon}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'w-2 h-0.5 mx-0.5',
                    isCompleted ? 'bg-green-400' : 'bg-gray-200'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render the appropriate mini progress based on template
  const renderMiniProgress = (ncr: NonConformanceReport) => {
    if (ncr.status === 'deleted') {
      return <span className="text-gray-400 text-xs">Deleted</span>;
    }
    return ncrTemplate === 1
      ? <MiniProgressTemplate1 status={ncr.status} />
      : <MiniProgressTemplate2 status={ncr.status} ncr={ncr} />;
  };

  // Show loading while checking tab settings
  if (!tabSettingsLoaded || (loading && !ncrs.length)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If NCRs tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.ncrs) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Quick-filter chips (All / Awaiting Supplier / Awaiting Customer / Appeals /
  // Closed) drive the same status/next_action/reject params as the detail
  // panel. Derive the active chip from the current values, and apply a chip by
  // resetting all three mode params to that chip's set.
  const activeQuickMode: NCRQuickMode =
    archivedFilter === 'true' ? 'archived'
    : rejectFilter === 'pending' ? 'appeals'
    : nextActionFilter === 'awaiting_supplier' ? 'awaiting_supplier'
    : nextActionFilter === 'awaiting_customer' ? 'awaiting_customer'
    : statusFilter === 'closed' ? 'closed'
    : (!statusFilter && !rejectFilter && !nextActionFilter) ? 'all'
    : 'all';

  // Archive/unarchive and delete are customer-admin actions. `is_company_admin`
  // is the user API's view of `profile.role.is_admin_role` (same derivation as
  // lib/newsAccess). Helix internals count too — they operate every tenant.
  const isCustomerAdmin = Boolean(
    user && (
      user.company_type === 'helix'
      || (user.company_type === 'customer' && user.is_company_admin)
    ),
  );

  // Archiving or deleting changes both the page of rows and every chip count,
  // and neither refetches on its own: the list keys off the filter params and
  // the counts off a JSON key of the same params, none of which move when a
  // row's own state changes. Bumping this drives both.
  const handleRowActionDone = () => {
    fetchData();
    setCountsRefreshKey((k) => k + 1);
  };

  const applyQuickMode = (mode: NCRQuickMode) => {
    const params = NCR_QUICK_MODES.find((m) => m.key === mode)?.params ?? {};
    setStatusFilter(params['status'] ?? '');
    setRejectFilter(params['reject'] ?? '');
    setNextActionFilter(params['next_action'] ?? '');
    setArchivedFilter(params['archived'] ?? '');
    pagination.reset();
  };

  // Base params for the chip counts — everything except the mode keys.
  const quickBaseParams: Record<string, unknown> = {
    search: debouncedSearchTerm || undefined,
    supplier: supplierFilter || undefined,
    'supplier__group': supplierGroupFilter || undefined,
    site: siteFilter || undefined,
    liability_type: liabilityFilter || undefined,
    part_number__icontains: partNumberFilter || undefined,
    quality_engineer: qualityEngineerFilter || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    po: poDeepLink.poId || undefined,
    ...getViewQueryParams(),
  };

  return (
    <div className="space-y-0">
      {/* Section Tour Trigger */}
      <div className="flex justify-end mb-4">
        <SectionTourTrigger
          pageKey="quality"
          sectionKey="ncrs"
          variant="link"
          label="Tour this page"
        />
      </div>

      {/* Main Card Container - matches old SupplierPortal layout */}
      <Card className="rounded-lg shadow-sm" data-tour="ncr-table">
        {/* Navigation Tabs Header */}
        <CardHeader className="border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <nav className="flex space-x-2">
              {tabVisibility.ncrs && (
                <Link
                  to="/quality"
                  className="px-4 py-2 text-sm font-medium rounded-md border-2 border-primary bg-primary text-primary-foreground shadow-sm"
                >
                  NCRs
                </Link>
              )}
              {showConcessionsTab && (
                <Link
                  to="/concessions"
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  Production Permit / Concession
                </Link>
              )}
              {tabVisibility.approval_register && (
                <Link
                  to="/quality/approval-register"
                  className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
                >
                  Approval Register
                </Link>
              )}
            </nav>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Toolbar Section */}
          <div className="border-b bg-white px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Left side - Add button (customer-only; suppliers cannot raise NCRs) */}
              <div>
                {isCustomer && (
                  <Button
                    onClick={handleAddNCR}
                    className="bg-[#1bc5bd] hover:bg-[#0bb4ac] text-white font-semibold uppercase text-sm px-6 py-2"
                    data-tour="add-ncr-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    ADD
                  </Button>
                )}
              </div>

              {/* Right side - Export and Filter buttons */}
              <div className="flex items-center gap-3">
                {/* CR-5887 (TEMPORARY — remove after testing): Helix-admin only. */}
                <SendQualitySummaryTestButton />
                <Button
                  variant="default"
                  onClick={handleExportExcel}
                  className="bg-primary hover:bg-primary/90"
                  disabled={exporting}
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                  )}
                  {exporting ? 'Exporting...' : 'Export to Excel'}
                </Button>
                <Button
                  variant="default"
                  onClick={() => setShowFilters(!showFilters)}
                  className="bg-primary hover:bg-primary/90"
                  data-tour="ncr-filters"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                  {showFilters ? (
                    <ChevronUp className="h-4 w-4 ml-2" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-2" />
                  )}
                </Button>
                <QualityColumnsDropdown columns={effectiveColumns} onChange={setColumns} />
              </div>
            </div>
          </div>

          {/* Quick-filter chips — key modes only, incl. Appeals (supplier
              rejection requested, awaiting the customer's decision). */}
          <div className="px-4 pb-3 pt-1 border-b">
            <NCRPODeepLinkChip deepLink={poDeepLink} onClear={() => { poDeepLink.clear(); pagination.reset(); }} />
            <NCRQuickFilters
              activeMode={activeQuickMode}
              onSelect={applyQuickMode}
              isCustomerAdmin={isCustomerAdmin}
              baseParams={quickBaseParams}
              refreshKey={countsRefreshKey}
            />
          </div>

          {/* Collapsible Filter Panel — CR-5550 round 2 extracted into
              NCRFiltersBar to keep this file under the size budget and
              to surface Site / Supplier Group / Supplier / Commodity
              Group / Commodity Type / Strategic alongside the legacy
              Search / Status / From / To controls. */}
          {showFilters && (
            <NCRFiltersBar
              values={{
                searchTerm,
                statusFilter,
                dateFrom,
                dateTo,
                siteFilter,
                supplierGroupFilter,
                supplierFilter,
                commodityGroupFilter,
                commodityTypeFilter,
                strategicFilter,
                partNumberFilter,
                regionFilter,
                liabilityFilter,
                warehouseFilter,
                claimStatusFilter,
                actionStatusFilter,
                collectionDateFrom,
                collectionDateTo,
              }}
              onChange={(next) => {
                if (next.searchTerm !== undefined) setSearchTerm(next.searchTerm);
                if (next.statusFilter !== undefined) { setStatusFilter(next.statusFilter); pagination.reset(); }
                if (next.dateFrom !== undefined) { setDateFrom(next.dateFrom); pagination.reset(); }
                if (next.dateTo !== undefined) { setDateTo(next.dateTo); pagination.reset(); }
                if (next.siteFilter !== undefined) { setSiteFilter(next.siteFilter); pagination.reset(); }
                if (next.supplierGroupFilter !== undefined) { setSupplierGroupFilter(next.supplierGroupFilter); pagination.reset(); }
                if (next.supplierFilter !== undefined) { setSupplierFilter(next.supplierFilter); pagination.reset(); }
                if (next.commodityGroupFilter !== undefined) { setCommodityGroupFilter(next.commodityGroupFilter); pagination.reset(); }
                if (next.commodityTypeFilter !== undefined) { setCommodityTypeFilter(next.commodityTypeFilter); pagination.reset(); }
                if (next.strategicFilter !== undefined) { setStrategicFilter(next.strategicFilter); pagination.reset(); }
                if (next.partNumberFilter !== undefined) { setPartNumberFilter(next.partNumberFilter); pagination.reset(); }
                if (next.regionFilter !== undefined) { setRegionFilter(next.regionFilter); pagination.reset(); }
                if (next.liabilityFilter !== undefined) { setLiabilityFilter(next.liabilityFilter); pagination.reset(); }
                if (next.warehouseFilter !== undefined) { setWarehouseFilter(next.warehouseFilter); pagination.reset(); }
                if (next.claimStatusFilter !== undefined) { setClaimStatusFilter(next.claimStatusFilter); pagination.reset(); }
                if (next.actionStatusFilter !== undefined) { setActionStatusFilter(next.actionStatusFilter); pagination.reset(); }
                if (next.collectionDateFrom !== undefined) { setCollectionDateFrom(next.collectionDateFrom); pagination.reset(); }
                if (next.collectionDateTo !== undefined) { setCollectionDateTo(next.collectionDateTo); pagination.reset(); }
              }}
              onClear={clearFilters}
              // CR-6451: the supplier design omits the strategic-suppliers
              // tickbox — a supplier filtering on that is filtering on
              // themselves.
              showStrategicFilter={isCustomer}
            />
          )}

          {/* CR-5887 rework: active deep-link filter banner. When the user
              arrives from a Daily Quality Summary email link, show exactly
              which bucket the list is scoped to (and let them clear it). */}
          {(nextActionFilter || rejectFilter) && (
            <div className="px-6 pt-4">
              <div className="flex items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
                <span className="flex flex-wrap items-center gap-1">
                  <Filter className="mr-1 h-4 w-4 flex-shrink-0" />
                  {nextActionFilter ? (
                    <>Filtered to <strong>Next Action: {NEXT_ACTION_LABELS[nextActionFilter] ?? nextActionFilter}</strong></>
                  ) : (
                    <>Filtered to <strong>{REJECT_LABELS[rejectFilter] ?? rejectFilter}</strong></>
                  )}
                  <span className="text-blue-600">— from your Daily Quality Summary email.</span>
                </span>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="flex flex-shrink-0 items-center gap-1 font-medium text-blue-700 hover:text-blue-900"
                >
                  <X className="h-4 w-4" /> Clear
                </button>
              </div>
            </div>
          )}

          {/* Data Table Section */}
          <div className="p-6">
            {ncrs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchTerm || statusFilter ? 'No NCRs found matching your criteria' : 'No NCRs found'}
              </div>
            ) : (
              <>
                {/* Top Pagination */}
                <div className="mb-4">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    itemsPerPage={pagination.pageSize}
                    onPageChange={pagination.goToPage}
                    itemLabel="NCRs"
                  />
                </div>

                {/* Table — columns + order driven by qualityColumnConfig (CR-DE-Quality-Cols).
                    CR-5547: switch to internal scrollable container with sticky
                    headers, mirroring the PO Line Items table layout, so column
                    headers stay visible during vertical scroll and horizontal
                    scrolling stays inside the table rather than the whole page.
                    Height budget matches POLinesTable (calc(100vh - 280px)). */}
                <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] border rounded-lg">
                  <table className="w-full">
                    <thead className="sticky top-0 z-20">
                      <tr className="bg-[#f3f6f9] border-b">
                        {visibleCols.map((col) => {
                          const sortKey = col.sortField || col.id;
                          return (
                            <th
                              key={col.id}
                              style={{ minWidth: col.minWidth }}
                              className={`text-left py-3 px-4 font-semibold text-sm text-[#3f4254] select-none ${
                                col.sortable ? 'cursor-pointer hover:bg-[#ebedf3]' : ''
                              }`}
                              onClick={col.sortable ? () => handleSort(sortKey) : undefined}
                            >
                              <div className="flex items-center">
                                {col.label}
                                {col.sortable && getSortIcon(sortKey)}
                                {columnFilters[col.id] && (
                                  <ColumnHeaderFilter label={col.label} {...columnFilters[col.id]} />
                                )}
                              </div>
                            </th>
                          );
                        })}
                        <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254] min-w-[160px]">
                          Progress
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-sm text-[#3f4254] min-w-[140px]">
                          Next Action
                        </th>
                        {isCustomerAdmin && (
                          <th className="text-right py-3 px-4 font-semibold text-sm text-[#3f4254] w-[60px]">
                            <span className="sr-only">Actions</span>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {ncrs.map((ncr, index) => (
                        <tr
                          key={ncr.id}
                          className={`border-b cursor-pointer transition-colors hover:bg-[#f3f6f9] ${
                            index % 2 === 0 ? 'bg-white' : 'bg-[#f9fafb]'
                          }`}
                          style={{ height: '58px' }}
                          onClick={() => handleViewNCR(ncr.id)}
                        >
                          {visibleCols.map((col) => (
                            <td
                              key={col.id}
                              className={`py-3 px-4 text-sm text-[#3f4254] ${
                                col.id === 'part_number' ? 'font-mono' : ''
                              }`}
                            >
                              {col.id === 'status_display'
                                ? getStatusBadge(ncr.status, ncr.status_display)
                                : renderNCRCell(col, ncr, { formatDate, formatNumber })}
                            </td>
                          ))}
                          <td className="py-3 px-4">{renderMiniProgress(ncr)}</td>
                          <td className="py-3 px-4 text-sm text-[#3f4254]">{getNextAction(ncr)}</td>
                          {isCustomerAdmin && (
                            <td className="py-3 px-4 text-right">
                              <NCRRowActions ncr={ncr} onChanged={handleRowActionDone} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Bottom Pagination */}
                <div className="mt-4">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    itemsPerPage={pagination.pageSize}
                    onPageChange={pagination.goToPage}
                    itemLabel="NCRs"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
